import { ColoursObj, Typography } from "@eventr/common-storybook";
import { Stack } from "@mui/material";
import { memo, useMemo } from "react";
import Markdown, { Components, defaultUrlTransform } from "react-markdown";
import remarkGemoji from "remark-gemoji";
import remarkGfm from "remark-gfm";
import remarkSmartypants from "remark-smartypants";
import { openBrowser } from "../../../utils";
import { ErrorBoundary } from "@sentry/react";
import rehypeRaw from "rehype-raw";

interface MarkdownRowProps {
  value: string;
  past?: boolean;
}

const markdownComponents = (extraProps: { past?: boolean } = {}): Components => {
  const { past } = extraProps;
  const colour = past ? ColoursObj.shades.shade2 : ColoursObj.font.primary;
  const themeProps = { color: colour };

  return {
    h1: ({ ...props }: { [key: string]: any }) => <Typography {...themeProps} variant="mobileH1" {...props} />,
    h2: ({ ...props }: { [key: string]: any }) => <Typography {...themeProps} variant="mobileH2" {...props} />,
    h3: ({ ...props }: { [key: string]: any }) => <Typography {...themeProps} variant="mobileH3" {...props} />,
    p: ({ ...props }: { [key: string]: any }) => <Typography {...themeProps} variant="bodyRegular" {...props} />,
    li: ({ ...props }: { [key: string]: any }) => (
      <Typography
        {...themeProps}
        component="li"
        variant="bodyRegular"
        sx={{ color: ColoursObj.font.primary }}
        {...props}
      />
    ),
    ul: ({ ...props }: { [key: string]: any }) => (
      <Typography
        {...themeProps}
        component="ul"
        variant="bodyRegular"
        sx={{ color: ColoursObj.font.primary, paddingLeft: "1.5rem" }}
        {...props}
      />
    ),
    strong: ({ ...props }: { [key: string]: any }) => <Typography {...themeProps} variant="bodyBold" {...props} />,
    em: ({ ...props }: { [key: string]: any }) => (
      <Typography {...themeProps} variant="bodyRegular" sx={{ fontStyle: "italic" }} {...props} />
    ),
    del: ({ ...props }: { [key: string]: any }) => (
      <Typography {...themeProps} variant="bodyRegularStrikethrough" {...props} />
    ),
    a: ({ ...props }: { [key: string]: any }) =>
      props.href && props.href.startsWith("tel:") ? (
        <a style={{ color: ColoursObj.core.secondary }} href={props.href} aria-label="open-telephone" {...props} />
      ) : (
        <Typography
          {...themeProps}
          variant="bodyRegularUnderlined"
          sx={{ color: ColoursObj.core.secondary }}
          onClick={props.onClick ? () => props.onClick(props.href) : () => openBrowser(props.href)}
          {...props}
        />
      ),
    hr: ({ ...props }: { [key: string]: any }) => (
      <hr
        style={{
          border: "none",
          borderTop: `1px solid ${ColoursObj.font.primary}`,
          margin: "1rem 0",
        }}
        {...props}
      />
    ),
    pre: ({ ...props }: { [key: string]: any }) => (
      <Typography
        variant="bodyRegular"
        sx={{
          backgroundColor: ColoursObj.backgrounds.primary,
          padding: "1rem",
          borderRadius: "0.5rem",
          overflowX: "auto",
        }}
        {...props}
      />
    ),

    code: ({ ...props }: { [key: string]: any }) => (
      <Typography
        variant="bodyRegular"
        sx={{
          backgroundColor: ColoursObj.backgrounds.primary,
          padding: "0.25rem 0.5rem",
          borderRadius: "0.25rem",
          fontFamily: "monospace",
        }}
        {...props}
      />
    ), // not dealing with syntax highlighting for now
    table: ({ ...props }: { [key: string]: any }) => (
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          margin: "1rem 0",
        }}
        {...props}
      />
    ),
    th: ({ ...props }: { [key: string]: any }) => (
      <Typography
        component="th"
        variant="bodyBold" // Use header 3 variant for table headers
        sx={{
          border: `1px solid ${ColoursObj.font.primary}`,
          padding: "0.5rem",
          textAlign: "left",
          backgroundColor: ColoursObj.backgrounds.secondary,
        }}
        {...props}
      />
    ),
    // br: ({ node, ...props }: { node?: unknown; [key: string]: any }) => (
    //     <br

    //     />
    // ),
    td: ({ ...props }: { [key: string]: any }) => (
      <Typography
        component="td"
        variant="bodyRegular"
        sx={{
          border: `1px solid ${ColoursObj.font.primary}`,
          padding: "0.5rem",
        }}
        {...props}
      />
    ),
    blockquote: ({ ...props }: { [key: string]: any }) => (
      <Typography
        variant="bodyRegular"
        sx={{
          borderLeft: `4px solid ${ColoursObj.font.primary}`,
          paddingLeft: "1rem",
          margin: "1rem 0",
          fontStyle: "italic",
        }}
        {...props}
      />
    ),
    img: ({ ...props }: { [key: string]: any }) => (
      <img
        style={{
          maxWidth: "100%",
          height: "auto",
          borderRadius: "0.5rem",
        }}
        alt={props.alt || ""}
        {...props}
      />
    ),
    sub: ({ ...props }: { [key: string]: any }) => (
      <Typography variant="bodyRegular" sx={{ fontSize: "0.8em", verticalAlign: "sub" }} {...props} />
    ),

    sup: ({ ...props }: { [key: string]: any }) => (
      <Typography variant="bodyRegular" sx={{ fontSize: "0.8em", verticalAlign: "super" }} {...props} />
    ),
  };
};
function preserveEmptyLines(md: string) {
  if (!md) return md;

  // Don't process newlines inside table structures
  // Tables have | characters and separator lines with --- or ===
  const lines = md.split("\n");
  const processedLines = [];

  for (let i = 0; i < lines.length; i++) {
    const currentLine = lines[i]!;
    const nextLine = lines[i + 1];

    // Check if current line is part of a table
    const isTableLine = currentLine.trim().startsWith("|") && currentLine.includes("|");
    const isTableSeparator =
      nextLine &&
      (nextLine.includes("---") || nextLine.includes("===")) &&
      nextLine.replace(/[-|\s]/g, "").length === 0;

    // Check if next line starts a table
    const nextLineStartsTable = nextLine && nextLine.trim().startsWith("|");

    if (isTableLine || isTableSeparator || nextLineStartsTable) {
      // Preserve original newlines for tables
      processedLines.push(currentLine);
    } else {
      // Apply empty line preservation for non-table content
      processedLines.push(currentLine);
      // This adds visual spacing for multiple consecutive empty lines
      processedLines.push("\n");
    }
  }

  return processedLines.join("\n");
}
// `past` is the only input the component map varies on, and rebuilding ~25 component closures per
// render meant react-markdown saw a new `components` object every time. The maps cannot simply be
// built once at module load, though: `setColourScheme` REASSIGNS `ColoursObj` to a whole new
// object, so anything that reads a colour at import time is pinned to the light default forever.
// Caching against the current `ColoursObj` identity keeps the reference stable between renders and
// rebuilds exactly once per theme change.
let cachedColours: unknown = null;
let cachedDefault: Components | null = null;
let cachedPast: Components | null = null;

const getMarkdownComponents = (past: boolean): Components => {
  if (cachedColours !== ColoursObj) {
    cachedColours = ColoursObj;
    cachedDefault = markdownComponents({ past: false });
    cachedPast = markdownComponents({ past: true });
  }
  return (past ? cachedPast : cachedDefault) as Components;
};

// Plugin arrays are identity-compared by react-markdown; inline literals defeated that.
const rehypePlugins = [rehypeRaw];
const remarkPlugins = [remarkGfm, remarkSmartypants, remarkGemoji];

const urlTransform = (url: string) => (url.startsWith("tel:") ? url : defaultUrlTransform(url));

/**
 * Memoised: a single activity card runs this over a description that averages ~900 characters on
 * an imported exhibitor list, and the full remark/rehype pipeline is the most expensive thing on
 * the card. Both props are primitives, so the memo comparison is exact.
 */
export const MarkdownRow = memo(function MarkdownRow({ value, past }: MarkdownRowProps) {
  // `preserveEmptyLines` roughly doubles the string, so it is worth not redoing per render.
  const processedValue = useMemo(() => (value ? preserveEmptyLines(value) : value), [value]);

  if (!value) return null;

  return (
    <Stack>
      <ErrorBoundary fallback={<Typography>{value}</Typography>}>
        <Markdown
          components={getMarkdownComponents(Boolean(past))}
          children={processedValue}
          rehypePlugins={rehypePlugins}
          remarkPlugins={remarkPlugins}
          urlTransform={urlTransform}
        />
      </ErrorBoundary>
    </Stack>
  );
});
